import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { HardHat, ArrowRight, Loader2 } from "lucide-react";

const PHASES = [
  { key: "land",       label: "Land",        desc: "Plot registered in your name." },
  { key: "foundation", label: "Foundation",  desc: "Site cleared, footings poured." },
  { key: "walls",      label: "Walls",       desc: "Brickwork + window frames." },
  { key: "roof",       label: "Roof",        desc: "Trusses + roofing + waterproofing." },
  { key: "finish",     label: "Finish",      desc: "Plaster, paint, doors, fittings." },
  { key: "complete",   label: "Complete",    desc: "Ready to live in." },
];

export default function BuildPhasePanel() {
  const [phase, setPhase] = useState("land");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/log/build-phase");
        if (data?.current_phase) setPhase(data.current_phase);
        if (data?.notes) setNotes(data.notes);
      } finally { setLoading(false); }
    })();
  }, []);

  const currentIdx = PHASES.findIndex((p) => p.key === phase);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/log/build-phase", { current_phase: phase, notes });
      toast.success("Build phase updated.");
    } catch { toast.error("Could not save."); } finally { setSaving(false); }
  };

  if (loading) return null;

  return (
    <div className="mv-card p-6" data-testid="build-phase-panel">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-2xl flex items-center justify-center mv-green-text" style={{ background: "rgba(212,175,55,0.16)" }}>
          <HardHat size={18}/>
        </div>
        <div>
          <div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>Phased Build</span></div>
          <div className="font-display text-lg mv-green-text">Track your build — one step at a time.</div>
        </div>
      </div>

      {/* Progress */}
      <div className="mt-2 flex items-center gap-2 overflow-x-auto pb-2">
        {PHASES.map((p, i) => (
          <React.Fragment key={p.key}>
            <button
              type="button"
              onClick={() => setPhase(p.key)}
              className={`shrink-0 flex flex-col items-center min-w-[92px] px-2 py-3 rounded-2xl border transition-colors ${i <= currentIdx ? "mv-green-bg text-white border-transparent" : "bg-white mv-hairline hover:bg-black/5"}`}
              data-testid={`phase-${p.key}`}
              title={p.desc}
            >
              <span className="text-[11px] uppercase tracking-[0.16em]">{`Phase ${i+1}`}</span>
              <span className="font-display text-[14px] mt-0.5">{p.label}</span>
            </button>
            {i < PHASES.length - 1 && <ArrowRight size={14} className="mv-green-text opacity-40 shrink-0"/>}
          </React.Fragment>
        ))}
      </div>

      <p className="mt-4 text-[13.5px]" style={{ color: "var(--mv-muted)" }}>
        <b className="mv-green-text">Now:</b> {PHASES[currentIdx].desc}
      </p>

      <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
        placeholder="Notes for this phase (materials, timing, contractors)…"
        className="mt-4 w-full px-3 py-2 rounded-xl border mv-hairline text-[13.5px]"
        data-testid="phase-notes"/>

      <button onClick={save} disabled={saving} className="btn-green mt-4" data-testid="phase-save">
        {saving ? <Loader2 className="animate-spin" size={16}/> : null}
        {saving ? "Saving…" : "Save Phase"}
      </button>
    </div>
  );
}
